// @ts-nocheck

import {
  Input,
  Button,
  Notify,
  useConnectWalletModal,
  useSwitchToConfiguredChain,
} from '@airdao/ui-library';
import React, { useEffect, useMemo, useState } from 'react';
import useBondContracts from '../../../contexts/BondContractContext/useBondContracts';
import {
  calculatedWillGet,
  calculateMax,
  handleAmount,
  purchaseBonds,
  willGet,
} from '../exchange.service';
import useBalance from '../../../hooks/getBalance';
import useMarkets from '../../../contexts/MarketsContext/useMarkets';
import { useParams } from 'react-router-dom';
import {
  MarketContextType,
  MarketType,
} from '../../../contexts/MarketsContext/provider.interface';
import { getTokenByAddress } from '../../../utils/getTokenByAddress';
import { timestampToDate } from '../../../utils/timestampToDate';
import { FPDA, SDA, vestingOptions } from '../../../utils/constants';
import { BigNumber, utils } from 'ethers';
// @ts-ignore
import { Methods, ContractNames } from '@airdao/airdao-bond';
import { formatDecimals } from '../../../utils/formatDecimals';
import useGetEngagementByAddress from '../../../hooks/useGetEngagementByAddress';
import useGetMyRewards from '../../../hooks/useGetMyRewards';
import { useAccount } from 'wagmi';

type FormProps = {
  marketData: MarketType;
  protocolFee: number;
};

const Form = ({ marketData, protocolFee }: FormProps) => {
  const { id } = useParams();
  const { address: account, chainId } = useAccount();
  const contracts = useBondContracts();
  const { balance, bnBalance } = useBalance(marketData.quoteToken);
  const { tokens }: MarketContextType = useMarkets();

  const { kosDiscount } = useGetEngagementByAddress()
  const stateRewards = useGetMyRewards();

  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isClosed, setIsClosed] = useState(false);

  const { toggleModal } = useConnectWalletModal();
  const switchToAmb = useSwitchToConfiguredChain();

  useEffect(() => {
    setError('');
  }, [amount]);

  const handleValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleAmount(e, setAmount, tokensData.quote?.contractAddress, tokens)
  };

  const tokensData = useMemo(() => {
    return {
      payout: getTokenByAddress(marketData.payoutToken, tokens),
      quote: getTokenByAddress(marketData.quoteToken, tokens),
    };
  }, [tokens]);

  const willGetSubFee = useMemo(() =>
    calculatedWillGet(marketData, amount, (+protocolFee - (+protocolFee * +stateRewards.getDiscount / 100))),
    [marketData, amount, protocolFee]
  )
  const handleSubmit = async () => {
    if (willGetSubFee.gte(BigNumber.from(marketData.maxPayout))) {
      setError('Enter amount less then max bondable value');
      return;
    } else if (utils.parseUnits(amount).gt(bnBalance)) {
      setError('Insufficient balance');
      return;
    } else if (willGet(amount, marketData.bondMarketPrice).gt(BigNumber.from(marketData.capacity))) {
      setError('Enter amount less then available amount');
      return;
    }
    if (tokensData && tokensData.quote) {
      Notify.loading('Transaction in progress.', null, {
        toastId: 'purchase-loading',
      });
      setIsLoading(true);

      try {
        await (
          await purchaseBonds(
            contracts,
            {
              recipient: account,
              referrer: account,
              id,
              amount: utils
                .parseUnits(amount, tokensData.quote.decimals)
                .toString(),
              minAmountOut: marketData.marketType === SDA ? willGetSubFee : '0',
              vestingType: marketData.vestingType,
            },
            tokensData.quote.contractAddress,
          )
        )
          .wait()
          .then(() => {
            Notify.success(
              `Success! You bought ${formatDecimals(
                formattedWillGetSubFee,
                tokensData.payout?.contractAddress,
                tokens,
              )} ${tokensData.payout?.symbol.toUpperCase()}`,
              null,
              { autoClose: 5000 },
            );
            setAmount('0');
          });
      } catch (e: any) {
        console.log(e, e.code);
        if (e.code !== 'ACTION_REJECTED') {
          Notify.error('Error', null, {});
        }
      }
      setAmount('');
      setIsLoading(false);
      Notify.dismiss('purchase-loading');
    }
  };

  const setMax = () => {
    let newAmount = calculateMax(marketData, bnBalance);
    if (marketData.marketType === FPDA) {
      newAmount = +newAmount - (+newAmount * +utils.formatEther(kosDiscount));
    }
    setAmount(
      formatDecimals(
        newAmount,
        tokensData.quote?.contractAddress,
        tokens,
      ).toString(),
    );
  };

  const handleMarketClose = async () => {
    if (contracts) {
      Notify.loading('Transaction in progress.', null, {
        toastId: 'close-loading',
      });

      const teller =
        marketData.vestingType === 'Fixed-expiry'
          ? ContractNames.FixedExpiryTeller
          : ContractNames.FixedTermTeller;

      try {
        setIsClosed(true);
        const tx = await Methods.bondTellerCloseMarket(
          contracts,
          teller,
          marketData.id,
        );
        await tx.wait();
        Notify.success(`Success!`, null, { autoClose: 5000 });
      } catch (e: any) {
        console.log(e);
        setIsClosed(false);
        if (e.code !== 'ACTION_REJECTED') {
          Notify.error('Error', null, {});
        }
      }
      Notify.dismiss('close-loading');
    }
  };

  const marketNotStarted = marketData.start > new Date().getTime() / 1000;
  const formattedWillGetSubFee = +utils.formatUnits(
    willGetSubFee,
    tokensData.quote?.decimals,
  );

  const isInvalidChain =
    process.env.REACT_APP_CHAIN_ID === chainId?.toString() || !account;

  const fee = (+amount * protocolFee) / 100;
  const calculatedProtocolFee = fee - (fee * +stateRewards.getDiscount / 100);

  return (
    <div className="exchange-form">
      <Input
        onChange={handleValue}
        placeholder="0"
        label="Cash"
        disabled={!marketData.isLive || marketNotStarted}
        value={amount}
        className="exchange-input"
        leadIcon={
          <span className="exchange-input__lead">
            {tokens.length && (
              <img src={tokensData?.quote?.logoUrl} alt="ambrosus" />
            )}
            {tokensData?.quote?.symbol.toUpperCase()}
          </span>
        }
      />
      <p className="exchange-form__max">
        Balance:{' '}
        {formatDecimals(balance, tokensData.quote?.contractAddress, tokens)}{' '}
        {tokensData?.quote?.symbol.toUpperCase()}{' '}
        <button onClick={setMax}>Max</button>
      </p>
      <div className="exchange-form__error">
        {error && (
          <span className="error-message error-message_balance">{error}</span>
        )}
      </div>
      <p className="exchange-form__row exchange-form__row_mt">
        <span className="exchange-form__row-label">You will get</span>
        <span className="exchange-form__row-black">
          {amount
            ? formatDecimals(
                formattedWillGetSubFee,
                tokensData.payout?.contractAddress,
                tokens,
              )
            : '-'}{' '}
          {tokensData?.payout?.symbol.toUpperCase()}
        </span>
      </p>
      <p className="exchange-form__row">
        <span className="exchange-form__row-label">Discount</span>
        <span
          className="exchange-form__row-black"
          style={{ color: marketData.discount > 0 ? 'green' : 'red' }}
        >
          {marketData.discount ? marketData.discount.toFixed(2) + '%' : '-'}
        </span>
      </p>
      <p className="exchange-form__row">
        {marketData.vestingType === 'Fixed-expiry' ? (
          <>
            <span className="exchange-form__row-label">
              Vesting expired date
            </span>
            <span className="exchange-form__row-black">
              {timestampToDate(+marketData.vesting)}
            </span>
          </>
        ) : (
          <>
            <span className="exchange-form__row-label">Lock period</span>
            <span className="exchange-form__row-black">
              {timestampToDate(new Date().getTime() / 1000)} to{' '}
              {timestampToDate(
                new Date().getTime() / 1000 + +marketData.vesting,
              )}
            </span>
            <span className="exchange-form__row-grey">
              (
              {
                vestingOptions.find(el => el.value === +marketData.vesting)
                  ?.label
              }
              )
            </span>
          </>
        )}
      </p>
      <p className="exchange-form__row">
        <span className="exchange-form__row-label">Max bondable</span>
        <span className="exchange-form__row-black">
          {formatDecimals(
            utils.formatUnits(
              marketData.maxPayout,
              tokensData.payout?.decimals,
            ),
            tokensData.payout?.contractAddress,
            tokens,
          )}{' '}
          {tokensData.payout?.symbol.toUpperCase()}
        </span>
        <span className="exchange-form__row-grey">
          $
          {(
            +utils.formatUnits(
              marketData.maxPayout,
              tokensData.payout?.decimals,
            ) * (tokensData.payout?.price || 0)
          ).toFixed(2)}
        </span>
      </p>
      <p className="exchange-form__row">
        <span className="exchange-form__row-label">Protocol fee</span>
        <span className="exchange-form__row-black">
          {formatDecimals(
            calculatedProtocolFee,
            tokensData.quote?.contractAddress,
            tokens,
          )}{' '}
          {tokensData.quote?.symbol.toUpperCase()}
        </span>
        <span className="exchange-form__row-grey">
          ${(calculatedProtocolFee * (tokensData.quote?.price || 0)).toFixed(2)}
        </span>
      </p>
      {account && process.env.REACT_APP_CHAIN_ID === chainId?.toString() ? (
        <>
          <Button
            type="primary"
            size="large"
            disabled={
              !+amount || !marketData.isLive || isLoading || marketNotStarted
            }
            className="exchange-form__submit"
            onClick={handleSubmit}
          >
            {marketData.isLive ? 'Enter amount to purchase' : 'Market closed'}
          </Button>
          {account === marketData.owner && marketData.isLive && (
            <Button
              type="tetiary"
              size="large"
              className="exchange-form__submit exchange-form__submit_mt"
              onClick={handleMarketClose}
              disabled={isClosed}
            >
              Close market
            </Button>
          )}
        </>
      ) : (
        <Button
          type="secondary"
          size="large"
          className="exchange-form__submit"
          onClick={
            isInvalidChain ? toggleModal : switchToAmb
          }
        >
          {isInvalidChain ? 'Connect wallet' : 'Switch to AMB-NET'}
        </Button>
      )}
    </div>
  );
};

export default Form;
